import { useEffect, useMemo, useState } from 'react';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import {
  EditIcon,
  EyeIcon,
  PlusIcon,
  RefreshCwIcon,
  ShoppingCartIcon,
  StoreIcon,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';

import { DialogDeleteRecordConfirmation } from '../../../components/pages/backoffice/dialogs/delete-record.jsx';
import { BackOfficePermissionsWrapper } from '../../../components/pages/backoffice/permissions-wrapper.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../components/table/data-table-column-header.jsx';
import {
  DataTableCampaignSelectFilter,
  DataTableSelectFilter,
} from '../../../components/table/data-table-filters.jsx';
import { DataTableFooter } from '../../../components/table/data-table-footer.jsx';
import {
  DataTableFacetedFilter,
  DataTableToolbar,
} from '../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Badge } from '../../../components/ui/badge.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Checkbox } from '../../../components/ui/checkbox.jsx';
import { DebouncedInput } from '../../../components/ui/input-debounced.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
// import { campaignState } from '../../../services/state/campaign-state.jsx';
import { useQueryActiveCampaign } from '../../../hooks/queries/use-active-campaign.jsx';
import {
  INVOICES_STATES,
  TABLE_FILTER_LIKE,
} from '../../../utils/constants.js';
import { filterIgnoreAccents, filterParams } from '../../../utils/filters.js';

// Query string keys of the deep links pointing to this page that do not match
// the respective table column id.
const SEARCH_PARAM_COLUMN_IDS = {
  campaignId: 'invoices.campaignId',
};

const TABLE_COLUMNS = [
  {
    id: 'select',
    header: ({ table }) => (
      <div className="pr-2">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Selecionar tudo"
          className="translate-y-[2px]"
        />
      </div>
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Selecionar linha"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Código',
    accessorKey: 'code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Código" />
    ),
    cell: ({ row }) => {
      return (
        <Link
          to={
            row.original.status === 'Pendente'
              ? `${row.original.id}/edit`
              : row.original.id
          }
        >
          <div className="underline">
            {row.getValue('code') || row.original.id}
          </div>
        </Link>
      );
    },
    enableSorting: false,
    enableHiding: false,
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    title: 'Estado',
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const invoiceStatus = row.getValue('status');

      return invoiceStatus === INVOICES_STATES.PENDING ? (
        <Badge
          className="bg-amber-500 hover:bg-amber-500 dark:text-stone-800"
          variant="secondary"
        >
          {invoiceStatus}
        </Badge>
      ) : invoiceStatus === INVOICES_STATES.APPROVED ? (
        <Badge className="min-w-24 justify-center" variant="success">
          {invoiceStatus}
        </Badge>
      ) : invoiceStatus === INVOICES_STATES.REJECTED ||
        invoiceStatus === INVOICES_STATES.VOIDED ? (
        <Badge className="min-w-24 justify-center" variant="destructive">
          {invoiceStatus}
        </Badge>
      ) : (
        <Badge className="min-w-24 justify-center" variant="outline">
          {invoiceStatus}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    title: 'Montante',
    accessorKey: 'amount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Montante" />
    ),
    cell: ({ row }) => {
      return <div>{row.getValue('amount')} €</div>;
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('createdTime');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd HH:mm:ss:SSS';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy HH:mm:ss')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Data de Emissão',
    accessorKey: 'issuedDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Emissão" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('issuedDate');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'customers.fullName',
    title: 'Cliente',
    accessorKey: 'customer',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Cliente" />
    ),
    cell: ({ row }) => {
      if (row.original.customerId) {
        return (
          <Link to={`/backoffice/customers/${row.original.customerId}`}>
            <div className="underline">{row.original.customer}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    id: 'invoices.campaignId',
    title: 'Campanha',
    accessorKey: 'campaign',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Campanha" />
    ),
    cell: ({ row }) => {
      if (row.original.campaignId) {
        return (
          <Link to={`/backoffice/campaigns/${row.original.campaignId}`}>
            <div className="underline">{row.original.campaign}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    id: 'shops.name',
    title: 'Loja',
    accessorKey: 'shop',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Loja" />
    ),
    cell: ({ row }) => {
      if (row.original.shopId) {
        return (
          <Link to={`/backoffice/admin/shops/${row.original.shopId}`}>
            <div className="underline">{row.original.shop}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.IN,
  },
  {
    title: 'staff',
    accessorKey: 'staff',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Alterado Por" />
    ),
    cell: ({ row }) => {
      return (
        <Link to={`/backoffice/admin/staff/${row.original.staffId}`}>
          <div className="underline">{row.getValue('staff')}</div>
        </Link>
      );
    },
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
  },
  {
    title: 'reason',
    accessorKey: 'reason',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Motivo (Rejeição ou Anulação)"
      />
    ),
    cell: ({ row }) => (
      <div className="min-w-72">
        <p className="line-clamp-2">{row.getValue('reason')}</p>
      </div>
    ),
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      return (
        <div className="flex flex-row gap-2">
          {row.original.status === 'Aprovada' ? (
            <Link to={row.original.id}>
              <Button
                variant="outline"
                className="h-auto p-2"
                aria-label="Ver fatura"
              >
                <EyeIcon />
              </Button>
            </Link>
          ) : (
            <BackOfficePermissionsWrapper>
              <Link to={`${row.original.id}/edit`}>
                <Button
                  variant="outline"
                  className="h-auto p-2"
                  aria-label="Editar fatura"
                >
                  <EditIcon />
                </Button>
              </Link>
            </BackOfficePermissionsWrapper>
          )}
        </div>
      );
    },
  },
];

export function InvoicesList() {
  const [searchParams] = useSearchParams();
  const { isAdmin } = useSnapshot(authState);
  // const { activeCampaign } = useSnapshot(campaignState);

  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: 'createdTime',
      desc: true,
    },
  ]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  const queryActiveCampaign = useQueryActiveCampaign();

  const queryInvoices = useQuery({
    queryKey: ['invoices', pagination, columnFilters, sorting],
    queryFn: async () => {
      let filters = filterParams(table);

      const { data: invoices } = await msCatalystApi.get(
        `/invoices/paginate?${filters}`,
      );

      return invoices.data;
    },
    placeholderData: keepPreviousData,
    select: (data) => {
      return {
        ...data,
        rows: data.rows.map((item) => ({
          id: item.invoices.ROWID,
          code: item.invoices.code,
          amount: item.invoices.amount,
          status: item.invoices.status,
          proof: item.invoices.proof,
          reason: item.invoices.reason,
          customer: `${item.customers.firstName} ${item.customers.lastName}`,
          customerId: item.customers.ROWID,
          campaigns: item.campaigns,
          campaign: item.campaigns.name,
          campaignId: item.campaigns.ROWID,
          shop: item.shops.name,
          shopId: item.shops.ROWID,
          staffId: item.staff.ROWID,
          staff: `${item.staff.firstName || ''} ${item.staff.lastName || ''}`,
          issuedDate: item.invoices.issuedDate,
          createdTime: item.invoices.CREATEDTIME,
        })),
      };
    },

    enabled: !queryActiveCampaign.isLoading,
  });

  const defaultData = useMemo(() => [], []);

  const table = useReactTable({
    data: queryInvoices.data?.rows ?? defaultData,
    columns: TABLE_COLUMNS,
    rowCount: queryInvoices.data?.rowCount,
    state: {
      pagination,
      sorting,
      columnFilters,
      rowSelection,
      columnVisibility,
    },
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: (val) => {
      setColumnFilters(val);
      if (val.length) setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableRowSelection: true,
    manualPagination: true,
  });

  const mutationDeleteInvoice = useMutation({
    mutationFn: async () => {
      const ids = table
        .getSelectedRowModel()
        .rows.map((rows) =>
          rows.original.ROWID ? rows.original.ROWID : rows.original.id,
        );

      const response = await msCatalystApi.delete(
        `/invoices?ids=${ids.join()}`,
      );

      return response.data;
    },
    onSuccess: async () => {
      toast.success(
        `${table.getSelectedRowModel().rows.length} registo(s) eliminado(s) com sucesso!`,
      );

      await queryInvoices.refetch();
    },
    onError: (err) => {
      toast.error('Erro ao eliminar registo(s)');
    },
  });

  useEffect(() => {
    if (
      queryActiveCampaign.isSuccess &&
      queryActiveCampaign?.data &&
      !columnFilters.length
    ) {
      setColumnFilters([
        {
          id: 'invoices.campaignId',
          value: [queryActiveCampaign.data?.ROWID],
        },
      ]);
    }
  }, [queryActiveCampaign.isSuccess]);

  useEffect(() => {
    if (searchParams.size > 0) {
      const paramFilters = [];

      searchParams.forEach((value, key) => {
        const columnId = SEARCH_PARAM_COLUMN_IDS[key] ?? key;

        if (!table.getColumn(columnId)) return;

        paramFilters.push({
          id: columnId,
          value: key === 'status' ? value.split(',') : [value],
        });
      });

      if (paramFilters.length) {
        setColumnFilters((prev) => [
          ...prev.filter(
            (filter) => !paramFilters.some((item) => item.id === filter.id),
          ),
          ...paramFilters,
        ]);
      }
    }
  }, [searchParams]);

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <Heading>Faturas</Heading>

        {queryActiveCampaign?.data ? (
          <div className="flex flex-row flex-wrap justify-end gap-4">
            <Button variant="outline" onClick={() => queryInvoices.refetch()}>
              <RefreshCwIcon />
              Atualizar
            </Button>

            <BackOfficePermissionsWrapper>
              <Link to="create">
                <Button>
                  <PlusIcon />
                  Inserir Fatura(s)
                </Button>
              </Link>
            </BackOfficePermissionsWrapper>
          </div>
        ) : null}
      </div>

      {queryInvoices.isLoading || queryActiveCampaign.isLoading ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryInvoices.isError ? (
        <Error />
      ) : (
        <div className="space-y-4">
          {/* TABLE HEADER */}
          <DataTableToolbar table={table}>
            <DebouncedInput
              placeholder={`Pesquisar código`}
              value={table.getColumn('code')?.getFilterValue() ?? ''}
              onChange={(val) => table.getColumn('code')?.setFilterValue(val)}
              className="h-10 md:w-[255px]"
            />

            {table.getColumn('status') && (
              <DataTableFacetedFilter
                column={table.getColumn('status')}
                title="Estado"
                search={false}
                options={[
                  { label: 'Pendente', value: INVOICES_STATES.PENDING },
                  { label: 'Aprovada', value: INVOICES_STATES.APPROVED },
                  { label: 'Rejeitada', value: INVOICES_STATES.REJECTED },
                  { label: 'Anulada', value: INVOICES_STATES.VOIDED },
                ]}
              />
            )}

            {isAdmin && table.getColumn('invoices.campaignId') && (
              <DataTableCampaignSelectFilter
                column={table.getColumn('invoices.campaignId')}
                title="Campanhas"
                table="campaigns"
                tableFilterCol="name"
                valueCol="ROWID"
                query={queryActiveCampaign}
              />
            )}

            {table.getColumn('customers.fullName') && (
              <DataTableSelectFilter
                column={table.getColumn('customers.fullName')}
                title="Clientes"
                table="customers"
                tableFilterCol="fullName"
                icon={<ShoppingCartIcon />}
              />
            )}

            {table.getColumn('shops.name') && (
              <DataTableSelectFilter
                column={table.getColumn('shops.name')}
                title="Lojas"
                table="shops"
                tableFilterCol="name"
                icon={<StoreIcon />}
              />
            )}
          </DataTableToolbar>

          {/* TABLE SELECTION SECTION */}
          {!!table.getSelectedRowModel().rows.length && isAdmin ? (
            <div className="flex w-full items-center justify-between rounded-md border border-muted-foreground/35 bg-secondary p-4">
              <span className="text-sm">
                {table.getSelectedRowModel().rows.length} fatura(s)
                selecionada(s)
              </span>

              <div className="flex items-center gap-4">
                <DialogDeleteRecordConfirmation
                  button={<Button variant="destructive">Eliminar</Button>}
                  action={() => mutationDeleteInvoice.mutateAsync()}
                  message="Todos os registos selecionados seram eliminados
            permanentemente do sistema."
                  alerts={false}
                />
              </div>
            </div>
          ) : null}

          {/* TABLE */}
          <div className="rounded-md border border-muted-foreground/35">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {queryInvoices.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryInvoices.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      {table.getState().columnFilters.length
                        ? 'Não foram encontrados resultados.'
                        : 'Não existem quaisquer Faturas na base de dados.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {/* TABLE */}
          {/* TABLE FOOTER */}
          <DataTableFooter table={table} />
          {/* TABLE FOOTER */}
        </div>
      )}
    </>
  );
}
